/**
 * src/ui/reglamento-modal.js — modal "Reglamento completo" del juego.
 *
 * Se abre desde el botón "Ver reglamento completo" de la card "Sistema de
 * Puntos" en la pantalla del Pacto (src/modules/pacto.js). A diferencia de
 * como-se-juega-modal.js, este NO tiene auto-open ni flag: solo se abre con el
 * botón y se cierra por X / CTA / tap en el backdrop.
 *
 * Mismo lenguaje visual y mismo patrón que como-se-juega-modal.js (header
 * sticky + body scrolleable de mini-cards + footer sticky con CTA, glass,
 * parser applyBold). Clases con prefijo rgl- y CSS propio inyectado una vez,
 * para no acoplarse al modal "Cómo se juega" (que ya está en producción).
 *
 * Copy FINAL aprobado por el dueño — NO reescribir. Cada renglón es una línea.
 */

const MODAL_ID = 'reglamentoModal';
const STYLE_ID = 'reglamento-modal-css';

// Copy canónico — NO reescribir. **bold** se renderiza vía applyBold.
const SECCIONES = [
    {
        titulo: '🤝 EL PACTO',
        lineas: [
            'El creador del grupo es el editor del pacto y puede delegar el rol a otro jugador.',
            'Los demás jugadores pueden proponer premios o castigos.',
            'Define **PREMIO** al campeón y **CASTIGO** al último (pozo o pacto de honor).',
            'Debe firmar al menos la mitad del grupo para poder certificarlo.',
            'Si el editor cambia premio o castigo, las firmas se reinician.',
            'La certificación es irreversible: al **CERTIFICAR** se desbloquea el **ranking del grupo** y el grupo puede publicar sus rituales en la **Tribuna Pública**.',
            'Buscá tu cupón de certificación en las redes.',
            '⚠️ **PACTO MUNDIAL NUNCA GESTIONA FONDOS NI DINERO DE TERCEROS**.',
            'La modalidad pozo es de exclusiva gestión de los jugadores: la app solo informa lo acordado.',
        ],
    },
    {
        titulo: '⚽ PRODE — FASE DE GRUPOS',
        lineas: [
            'Podés jugar ya, todos los partidos listados.',
            'Jugás a gana - empata - pierde.',
            'Podés editar tus predicciones hasta instantes previos al comienzo del partido.',
            '**+5** acertás el ganador.',
            '**+1** acertás el empate.',
            '**0** error leve (jugaste empate y hubo ganador, o al revés).',
            '**−1** error contrario (jugaste al que perdió).',
            '**0** no jugás.',
        ],
    },
    {
        titulo: '⚽ PRODE — PLAYOFFS',
        lineas: [
            'Los equipos deben estar definidos para poder predecir.',
            'Predecís los goles de cada equipo.',
            'Si jugás empate, elegís quién pasa por penales (los penales no cuentan como goles).',
            '**0** si no acertás quién pasa de fase, no sumás nada en ese partido.',
            '**+10** acertás quién pasa de fase.',
            '**+10** si además acertás la diferencia de gol.',
            '**+10** si además acertás los goles del ganador.',
            '**+50** de bonus por acierto completo.',
            'Máximo: **80 puntos** por partido.',
        ],
    },
    {
        titulo: '🔥 MULTIPLICADORES',
        lineas: [
            'En playoffs los puntos se multiplican por fase',
            '16avos **x1** · Octavos **x2** · Cuartos **x4** · Semis **x8** · Final **x12**',
            'Tercer puesto: x1',
            '🇦🇷 Si juega Argentina en playoffs: todo **x3**',
        ],
    },
    {
        titulo: '📣 RITUALES',
        lineas: [
            '**Arengas:** audios de hasta 20 segundos.',
            '**Cábalas:** se activan por un tiempo determinado.',
            '**Promesas:** deben cumplirse en el tiempo declarado y validarse por otro jugador del grupo.',
            '**+1** por crear un ritual.',
            'Las reacciones **SOLO** suman puntos para el creador.',
            { tabla: [
                ['1 a 10', '+1 por reacción'],
                ['11 a 1.000', '+1 cada 10 reacciones'],
                ['1.001 a 10.000', '+1 cada 100 reacciones'],
                ['10.001 a 100.000', '+1 cada 1.000 reacciones'],
                ['100.001 a 1.000.000', '+1 cada 10.000 reacciones'],
            ] },
            '⚠️ Promesa incumplida: **−100** — tu puntaje puede quedar en negativo.',
        ],
    },
    {
        titulo: '🌎 TRIBUNA PÚBLICA',
        lineas: [
            'Con el pacto certificado, podés publicar tus rituales en la Tribuna Pública.',
            'Las reacciones de cualquier jugador suman igual que las del grupo.',
            '🚀 Un ritual viral puede dar vuelta el ranking.',
        ],
    },
    {
        titulo: '📈 RANKING',
        lineas: [
            '**General = Prode + Rituales**',
            '**Mi Grupo:** vos y tus amigos',
            '**Intergrupal:** el promedio de puntos de tu grupo compite contra otros grupos',
            '**Nacional:** vos, individualmente, en la tabla de todo el país',
            '🥇 Más puntos: **campeón** · 🥄 Menos puntos: **castigo**',
        ],
    },
];

// Convierte **así** en <strong>así</strong>. Non-greedy, varios por línea.
const applyBold = (linea) => linea.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

// Renderiza un renglón: string → <p>; objeto { tabla } → mini-tabla 2 columnas.
const renderLinea = (l) => {
    if (typeof l === 'string') return `<p class="rgl-section-line">${applyBold(l)}</p>`;
    if (l && Array.isArray(l.tabla)) {
        return `<div class="rgl-table">${l.tabla.map(([k, v]) =>
            `<div class="rgl-table-key">${k}</div><div class="rgl-table-val">${v}</div>`).join('')}</div>`;
    }
    return '';
};

// HTML puro del modal — exportado para test.
export const buildReglamentoModalHTML = () => {
    const secciones = SECCIONES.map((s) => `
        <div class="rgl-section">
            <h3 class="rgl-section-title">${s.titulo}</h3>
            ${s.lineas.map(renderLinea).join('')}
        </div>
    `).join('');

    return `
        <div id="${MODAL_ID}" class="rgl-overlay" role="dialog" aria-modal="true" aria-label="Reglamento">
            <div class="rgl-card glass-card">
                <header class="rgl-header">
                    <h2 class="rgl-title">📜 REGLAMENTO</h2>
                    <button class="rgl-close" type="button" aria-label="Cerrar">✕</button>
                </header>
                <div class="rgl-body">
                    ${secciones}
                </div>
                <footer class="rgl-footer">
                    <button class="btn btn-primary rgl-cta" type="button">¡A jugar!</button>
                </footer>
            </div>
        </div>
    `;
};

// CSS inyectado una sola vez (idempotente). Mismo lenguaje visual que csj-*.
const MODAL_CSS = `
    .rgl-overlay {
        position: fixed; inset: 0; z-index: 100;
        background: rgba(0,0,0,.68); backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 1rem; animation: rglModalIn .18s ease-out;
    }
    @keyframes rglModalIn { from { opacity: 0; } to { opacity: 1; } }
    .rgl-card {
        position: relative; width: min(100%, 420px); max-height: calc(100dvh - 2rem);
        display: flex; flex-direction: column; padding: 0; overflow: hidden;
        border-radius: 16px;
        background: radial-gradient(circle at 14% 0%, rgba(117,170,219,.12), transparent 40%), rgba(12,18,30,.98);
        border: 1px solid rgba(117,170,219,.22);
    }
    .rgl-header {
        flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
        gap: .6rem; padding: 1rem 1.1rem .8rem;
        border-bottom: 1px solid rgba(255,255,255,.07);
        background: rgba(12,18,30,.6);
    }
    .rgl-title {
        margin: 0; color: var(--white);
        font-family: var(--font-heading); font-size: 1.02rem;
        letter-spacing: .04em; line-height: 1.1; text-transform: uppercase;
    }
    .rgl-close {
        flex: 0 0 auto; width: 30px; height: 30px; border-radius: 999px;
        border: 1px solid rgba(255,255,255,.14); background: rgba(255,255,255,.05);
        color: var(--white); font-size: .85rem; cursor: pointer;
        display: inline-flex; align-items: center; justify-content: center;
    }
    .rgl-close:hover { background: rgba(255,255,255,.1); }
    .rgl-body {
        flex: 1 1 auto; overflow-y: auto; padding: .9rem 1.1rem;
        display: flex; flex-direction: column; gap: .6rem;
    }
    .rgl-section {
        padding: .7rem .8rem; border-radius: 11px;
        background: rgba(255,255,255,.035); border: 1px solid rgba(255,255,255,.07);
    }
    .rgl-section-title {
        margin: 0 0 .4rem; color: var(--white);
        font-family: var(--font-heading); font-size: .92rem;
        font-weight: 900; letter-spacing: .03em;
    }
    .rgl-section-line {
        margin: 0 0 .22rem; color: var(--text-muted);
        font-size: .82rem; line-height: 1.34;
    }
    .rgl-section-line:last-child { margin-bottom: 0; }
    .rgl-section-line strong { color: var(--white); font-weight: 800; }
    .rgl-table {
        display: grid; grid-template-columns: auto 1fr; gap: 1px;
        margin: .4rem 0 .5rem; border-radius: 8px; overflow: hidden;
        background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.08);
    }
    .rgl-table-key, .rgl-table-val {
        padding: .34rem .6rem; font-size: .78rem; line-height: 1.2;
        background: rgba(12,18,30,.72);
    }
    .rgl-table-key { color: var(--text-muted); white-space: nowrap; }
    .rgl-table-val { color: var(--white); font-weight: 700; }
    .rgl-footer {
        flex: 0 0 auto; padding: .8rem 1.1rem 1rem;
        border-top: 1px solid rgba(255,255,255,.07);
        background: rgba(12,18,30,.6);
    }
    .rgl-cta { width: 100%; margin: 0; }
`;

const injectCSS = () => {
    if (typeof document === 'undefined' || !document.head) return;
    try {
        if (!document.getElementById?.(STYLE_ID)) {
            const styleEl = document.createElement('style');
            styleEl.id = STYLE_ID;
            styleEl.textContent = MODAL_CSS;
            document.head.appendChild?.(styleEl);
        }
    } catch { /* polyfills mínimos de test no tienen head completo — no romper imports */ }
};
injectCSS();

const escListener = (e) => { if (e.key === 'Escape') closeReglamentoModal(); };

export const openReglamentoModal = () => {
    if (typeof document === 'undefined') return;
    closeReglamentoModal();   // idempotente: nunca dos modales
    injectCSS();              // defensivo si el import corrió sin head
    const container = document.createElement('div');
    container.innerHTML = buildReglamentoModalHTML();
    const modal = container.firstElementChild;
    if (!modal) return;
    document.body.appendChild(modal);
    modal.querySelector('.rgl-close')?.addEventListener('click', closeReglamentoModal);
    modal.querySelector('.rgl-cta')?.addEventListener('click', closeReglamentoModal);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeReglamentoModal(); });
    document.addEventListener('keydown', escListener);
};

export const closeReglamentoModal = () => {
    if (typeof document === 'undefined') return;
    document.getElementById(MODAL_ID)?.remove();
    document.removeEventListener?.('keydown', escListener);
};

// Bridges para el onclick="" inline del botón en pacto.js.
if (typeof window !== 'undefined') {
    window.openReglamentoModal = openReglamentoModal;
    window.closeReglamentoModal = closeReglamentoModal;
}
